import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { getMainAxios } from '../utils/util';
import { useGridCopyMenu } from './gridCommon';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);
Highcharts.setOptions({ time: { useUTC: false } });

export interface IntradayTraceConfig {
  prodId: string;
  fromDate: string;  // yyyy-MM-dd
  toDate: string;    // yyyy-MM-dd
  barResolution: number;   // 1/3/5/10/15/30/60
  signalFrequency: number; // >= barResolution
}

interface Props {
  /** 초기 설정값 */
  initial?: Partial<IntradayTraceConfig>;
  /** 자동 실행 여부 (기본 false) */
  autoRun?: boolean;
  /** 패널 제목 */
  title?: string;
  /** 외부에서 설정 변경 반영 받고 싶을 때 */
  onConfigChange?: (cfg: IntradayTraceConfig) => void;
  /** 상단 컨트롤(종목/기간/Bar/Signal/실행 버튼) 표시 여부 (기본 true) */
  showControls?: boolean;
  /** 외부 제어 모드: 값이 주어지면 외부 설정을 그대로 사용 (controlled) */
  config?: IntradayTraceConfig;
  /** 외부에서 실행을 트리거하기 위한 신호값 (바뀔 때마다 재조회) */
  runTrigger?: number;
}

const NO_SF = {
  sortable: false, filter: false, resizable: true,
  suppressMenu: true, menuTabs: [] as any,
};

const IntradayTraceSection: React.FC<Props> = ({
  initial, autoRun = false,
  title = '실행 트레이스 (IntradayService 재현)',
  onConfigChange, showControls = true, config, runTrigger,
}) => {
  const axiosInstance = getMainAxios();
  const copyMenu = useGridCopyMenu();

  const today = new Date().toISOString().split('T')[0];
  const defaultFrom = (() => { const dt = new Date(); dt.setDate(dt.getDate() - 5); return dt.toISOString().split('T')[0]; })();

  const [prodIdState, setProdIdState] = useState<string>(initial?.prodId ?? '005930');
  const [fromDateState, setFromDateState] = useState<string>(initial?.fromDate ?? defaultFrom);
  const [toDateState, setToDateState] = useState<string>(initial?.toDate ?? today);
  const [barResolutionState, setBarResolutionState] = useState<number>(initial?.barResolution ?? 1);
  const [signalFrequencyState, setSignalFrequencyState] = useState<number>(initial?.signalFrequency ?? 5);

  // controlled 모드면 외부 config 사용, 아니면 내부 state
  const prodId = config?.prodId ?? prodIdState;
  const fromDate = config?.fromDate ?? fromDateState;
  const toDate = config?.toDate ?? toDateState;
  const barResolution = config?.barResolution ?? barResolutionState;
  const signalFrequency = config?.signalFrequency ?? signalFrequencyState;

  const setProdId = (v: string) => { if (!config) setProdIdState(v); };
  const setFromDate = (v: string) => { if (!config) setFromDateState(v); };
  const setToDate = (v: string) => { if (!config) setToDateState(v); };
  const setBarResolution = (v: number) => { if (!config) setBarResolutionState(v); };
  const setSignalFrequency = (v: number) => { if (!config) setSignalFrequencyState(v); };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);

  const [selectedTickTime, setSelectedTickTime] = useState<string | null>(null);
  const [tickWindowBars, setTickWindowBars] = useState<any[]>([]);
  const [tickWindowLoading, setTickWindowLoading] = useState(false);

  // 외부 알림
  useEffect(() => {
    onConfigChange?.({ prodId, fromDate, toDate, barResolution, signalFrequency });
  }, [prodId, fromDate, toDate, barResolution, signalFrequency]);

  const runTrace = useCallback(async () => {
    setLoading(true); setError(null);
    setSelectedTickTime(null); setTickWindowBars([]);
    try {
      const sf = Math.max(signalFrequency, barResolution);
      const res = await axiosInstance.get('/api/v1/quant/intraday/trace', {
        params: { prodId, fromDate, toDate, barResolution, signalFrequency: sf },
      });
      setData(res.data);
    } catch (err: any) {
      console.error('trace 조회 실패:', err);
      setError(err?.response?.data?.error || err?.message || '조회 실패');
    } finally {
      setLoading(false);
    }
  }, [prodId, fromDate, toDate, barResolution, signalFrequency]);

  const loadTickWindow = useCallback(async (tickTime: string) => {
    const parts = tickTime.includes(' ') ? tickTime.split(' ') : [toDate, tickTime];
    const tickDate = parts[0];
    const hm = parts[1];
    setSelectedTickTime(tickTime); setTickWindowLoading(true);
    try {
      const res = await axiosInstance.get('/api/v1/quant/intraday/trace-window', {
        params: { prodId, d: tickDate, tickTime: hm, barResolution },
      });
      setTickWindowBars(res.data?.bars ?? []);
    } catch (err) {
      console.error('window 조회 실패:', err);
      setTickWindowBars([]);
    } finally {
      setTickWindowLoading(false);
    }
  }, [prodId, toDate, barResolution]);

  // 파라미터 변경 시 자동 재실행 — 내부 제어 모드에서만
  useEffect(() => {
    if (config) return; // controlled 모드에선 외부 runTrigger 로 실행
    if (!data) return;
    runTrace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barResolution, signalFrequency]);

  // 초기 자동 실행
  useEffect(() => {
    if (autoRun) runTrace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 외부 runTrigger 변경 시 실행 (controlled)
  useEffect(() => {
    if (runTrigger == null) return;
    runTrace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runTrigger]);

  // ===== 차트 옵션 (Highstock 캔들 + Buy/Sell flags) =====
  const chartOptions = useMemo<Highcharts.Options | null>(() => {
    if (!data?.bars || data.bars.length === 0) return null;
    const bi = data.barResolution ?? 1;
    const fromStr: string = data.fromDate ?? data.d;
    const toStr: string = data.toDate ?? data.d;

    const toTs = (dateStr: string, hm: string): number => {
      const [yy, mo, dd] = dateStr.split('-').map(Number);
      const s = String(hm ?? '');
      if (s.length < 4) return 0;
      const hh = parseInt(s.slice(0, 2), 10);
      const mm = parseInt(s.slice(2, 4), 10);
      return new Date(yy, mo - 1, dd, hh, mm, 0).getTime();
    };

    const candles: number[][] = [];
    const vols: number[][] = [];
    for (const b of data.bars) {
      const ts = toTs(b.d, b.t);
      if (!ts) continue;
      candles.push([ts, Number(b.o) || 0, Number(b.h) || 0, Number(b.l) || 0, Number(b.c) || 0]);
      vols.push([ts, Number(b.v) || 0]);
    }
    if (candles.length === 0) return null;

    const buyFlags: any[] = [];
    const sellFlags: any[] = [];
    for (const dd of (data.tickDecisions ?? [])) {
      if (dd.decision !== 'Buy' && dd.decision !== 'Sell') continue;
      const parts = String(dd.tickTime).split(' ');
      if (parts.length < 2) continue;
      const ts = toTs(parts[0], parts[1].replace(':', '') + '00');
      if (!ts) continue;
      const label = dd.decision === 'Buy' ? 'B' : 'S';
      const point = {
        x: ts, title: label,
        text: `${dd.decision} @ ${Number(dd.price).toLocaleString()}<br/>B/S/H=${dd.buy}/${dd.sell}/${dd.hold}<br/>${dd.bestStrategy}: ${dd.bestReason ?? ''}`,
      };
      if (dd.decision === 'Buy') buyFlags.push(point); else sellFlags.push(point);
    }

    // 일자 경계 break
    const breaks: any[] = [];
    const ts = candles.map(c => c[0]).sort((a, b) => a - b);
    const barMs = bi * 60 * 1000;
    const gapThreshold = Math.max(barMs * 3, 8 * 60 * 1000);
    for (let i = 1; i < ts.length; i++) {
      const gap = ts[i] - ts[i - 1];
      if (gap > gapThreshold) breaks.push({ from: ts[i - 1] + barMs, to: ts[i] - barMs });
    }

    return {
      time: { timezone: 'Asia/Seoul' },
      title: { text: `${data.prodId} — ${bi}분봉 (${fromStr} ~ ${toStr}) [clsprc 기반]`,
        style: { fontSize: '13px' } },
      credits: { enabled: false },
      chart: { height: 460 },
      plotOptions: {
        series: { dataGrouping: { enabled: false } },
        candlestick: { color: '#2563eb', lineColor: '#2563eb', upColor: '#dc2626', upLineColor: '#dc2626' },
        flags: { shape: 'circlepin', width: 22, height: 22, y: -22,
          lineWidth: 2,
          style: { fontSize: '13px', fontWeight: 'bold', textOutline: '1px contrast' } },
      },
      xAxis: { type: 'datetime', breaks, labels: { style: { fontSize: '11px' } } },
      yAxis: [
        { labels: { align: 'right', x: -3, style: { fontSize: '11px' } },
          title: { text: 'OHLC', style: { fontSize: '11px' } },
          height: '65%', lineWidth: 1 },
        { labels: { align: 'right', x: -3, style: { fontSize: '11px' } },
          title: { text: 'Volume', style: { fontSize: '11px' } },
          top: '70%', height: '30%', offset: 0, lineWidth: 1 },
      ],
      series: [
        { type: 'candlestick', id: 'price', name: '가격', data: candles } as any,
        { type: 'column', name: '거래량', data: vols, yAxis: 1, color: '#93c5fd' } as any,
        { type: 'flags', name: 'Buy', data: buyFlags, onSeries: 'price',
          color: '#991b1b', fillColor: '#ef4444',
          style: { color: '#ffffff', fontSize: '13px', fontWeight: 'bold' } } as any,
        { type: 'flags', name: 'Sell', data: sellFlags, onSeries: 'price',
          color: '#1e3a8a', fillColor: '#3b82f6',
          style: { color: '#ffffff', fontSize: '13px', fontWeight: 'bold' },
          shape: 'circlepin', y: -46 } as any,
      ],
      rangeSelector: { enabled: false },
      navigator: { enabled: false },
      scrollbar: { enabled: false },
      tooltip: { split: false, shared: true, valueDecimals: 0 },
    };
  }, [data]);

  // ===== 그리드 컬럼 정의 =====
  const tickColumnDefs = useMemo(() => {
    if (!data?.ticks || data.ticks.length === 0) return [];
    const indKeys = new Set<string>();
    for (const t of data.ticks) {
      if (t.indicators) Object.keys(t.indicators).forEach(k => indKeys.add(k));
    }
    const dynamicIndCols = Array.from(indKeys).sort().map(k => ({
      headerName: k, field: `indicators.${k}`, width: 130, flex: 0, ...NO_SF,
      valueGetter: (p: any) => p.data?.indicators?.[k],
      valueFormatter: (p: any) => {
        if (typeof p.value !== 'number') return '';
        return Math.abs(p.value) >= 1000 ? p.value.toFixed(2) : p.value.toFixed(4);
      },
      cellStyle: { textAlign: 'right', fontFamily: 'monospace', fontSize: '11px' },
    }));
    return [
      { headerName: '시각', field: 'tickTime', width: 140, flex: 0, pinned: 'left', ...NO_SF,
        cellStyle: { textAlign: 'center', fontFamily: 'monospace' } },
      { headerName: '전략', field: 'strategy', width: 170, flex: 0, pinned: 'left', ...NO_SF },
      { headerName: '신호', field: 'signal', width: 70, flex: 0, ...NO_SF,
        cellStyle: (p: any) => ({
          textAlign: 'center', fontWeight: 700,
          color: p.value === 'Buy' ? '#dc2626' : p.value === 'Sell' ? '#2563eb' : '#6b7280',
        }) },
      { headerName: '가격', field: 'price', width: 90, flex: 0, ...NO_SF,
        valueFormatter: (p: any) => p.value ? Number(p.value).toLocaleString() : '',
        cellStyle: { textAlign: 'right' } },
      { headerName: 'conf', field: 'confidence', width: 75, flex: 0, ...NO_SF,
        valueFormatter: (p: any) => typeof p.value === 'number' ? p.value.toFixed(3) : '',
        cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
      { headerName: '사용바', field: 'windowBarCount', width: 80, flex: 0, ...NO_SF,
        valueFormatter: (p: any) => p.value ? Number(p.value).toLocaleString() : '',
        cellStyle: { textAlign: 'right', color: '#7c3aed', fontWeight: 600 } },
      ...dynamicIndCols,
      { headerName: '이유', field: 'reason', width: 320, flex: 0, ...NO_SF,
        cellStyle: { fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
        tooltipField: 'reason' },
    ];
  }, [data]);

  const decisionColumnDefs = useMemo(() => [
    { headerName: '시각', field: 'tickTime', width: 140, flex: 0, pinned: 'left', ...NO_SF,
      cellStyle: { textAlign: 'center', fontFamily: 'monospace' } },
    { headerName: '결정', field: 'decision', width: 70, flex: 0, ...NO_SF,
      cellStyle: (p: any) => ({
        textAlign: 'center', fontWeight: 700,
        color: p.value === 'Buy' ? '#dc2626' : p.value === 'Sell' ? '#2563eb' : '#6b7280',
      }) },
    { headerName: 'B', field: 'buy', width: 50, flex: 0, ...NO_SF,
      cellStyle: { textAlign: 'center', color: '#dc2626' } },
    { headerName: 'S', field: 'sell', width: 50, flex: 0, ...NO_SF,
      cellStyle: { textAlign: 'center', color: '#2563eb' } },
    { headerName: 'H', field: 'hold', width: 50, flex: 0, ...NO_SF,
      cellStyle: { textAlign: 'center', color: '#6b7280' } },
    { headerName: '가격', field: 'price', width: 90, flex: 0, ...NO_SF,
      valueFormatter: (p: any) => p.value ? Number(p.value).toLocaleString() : '',
      cellStyle: { textAlign: 'right' } },
    { headerName: 'best', field: 'bestStrategy', width: 170, flex: 0, ...NO_SF },
    { headerName: 'conf', field: 'bestConfidence', width: 75, flex: 0, ...NO_SF,
      valueFormatter: (p: any) => typeof p.value === 'number' ? p.value.toFixed(3) : '',
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: 'wei', field: 'wei', width: 75, flex: 0, ...NO_SF,
      valueFormatter: (p: any) => typeof p.value === 'number' ? p.value.toFixed(3) : '',
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' } },
    { headerName: '이유', field: 'bestReason', width: 320, flex: 0, ...NO_SF,
      cellStyle: { fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
      tooltipField: 'bestReason' },
  ], []);

  const barColumnDefs = useMemo(() => [
    { headerName: '날짜', field: 'd', width: 95, flex: 0, pinned: 'left', ...NO_SF,
      cellStyle: { textAlign: 'center', fontFamily: 'monospace' } },
    { headerName: '시각', field: 't', width: 90, flex: 0, pinned: 'left', ...NO_SF,
      valueFormatter: (p: any) => {
        const s = String(p.value ?? '');
        if (s.length >= 6) return `${s.slice(0,2)}:${s.slice(2,4)}:${s.slice(4,6)}`;
        if (s.length >= 4) return `${s.slice(0,2)}:${s.slice(2,4)}`;
        return s;
      },
      cellStyle: { textAlign: 'center', fontFamily: 'monospace' } },
    { headerName: '시가', field: 'o', width: 85, flex: 0, ...NO_SF,
      valueFormatter: (p: any) => p.value ? Number(p.value).toLocaleString() : '',
      cellStyle: { textAlign: 'right' } },
    { headerName: '고가', field: 'h', width: 85, flex: 0, ...NO_SF,
      valueFormatter: (p: any) => p.value ? Number(p.value).toLocaleString() : '',
      cellStyle: { textAlign: 'right', color: '#dc2626' } },
    { headerName: '저가', field: 'l', width: 85, flex: 0, ...NO_SF,
      valueFormatter: (p: any) => p.value ? Number(p.value).toLocaleString() : '',
      cellStyle: { textAlign: 'right', color: '#2563eb' } },
    { headerName: '종가', field: 'c', width: 85, flex: 0, ...NO_SF,
      valueFormatter: (p: any) => p.value ? Number(p.value).toLocaleString() : '',
      cellStyle: { textAlign: 'right', fontWeight: 600 } },
    { headerName: '거래량', field: 'v', width: 110, flex: 0, ...NO_SF,
      valueFormatter: (p: any) => p.value ? Number(p.value).toLocaleString() : '',
      cellStyle: { textAlign: 'right' } },
    { headerName: '거래대금', field: 'amt', width: 130, flex: 0, ...NO_SF,
      valueFormatter: (p: any) => p.value ? Number(p.value).toLocaleString() : '',
      cellStyle: { textAlign: 'right' } },
    { headerName: '틱수', field: 'tickCount', width: 60, flex: 0, ...NO_SF,
      cellStyle: { textAlign: 'right', color: '#7c3aed' } },
  ], []);

  const hdrStyle: React.CSSProperties = { margin: 0, fontSize: '12px', fontWeight: 600, color: '#1e40af' };
  const panelStyle: React.CSSProperties = {
    backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '14px',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* 그리드 헤더의 메뉴 아이콘 숨김 */}
      <style>{`
        .ag-theme-quartz .ag-header-cell-menu-button,
        .ag-theme-quartz .ag-header-icon.ag-header-cell-menu-button { display: none !important; }
        .ag-theme-quartz .ag-header-cell-label { padding-right: 2px; }
      `}</style>

      <div style={panelStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
          <h3 style={hdrStyle}>{title}{loading && <span style={{ marginLeft: 8, fontSize: '10px', color: '#6b7280' }}>실행중...</span>}</h3>
          {showControls && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '11px', flexWrap: 'wrap' }}>
              <label>종목:
                <input value={prodId} onChange={e => setProdId(e.target.value.trim())}
                  style={{ width: '75px', padding: '3px 6px', border: '1px solid #d1d5db',
                    borderRadius: '3px', fontSize: '11px', marginLeft: '4px' }} />
              </label>
              <label>기간:
                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                  style={{ padding: '3px 6px', border: '1px solid #d1d5db',
                    borderRadius: '3px', fontSize: '11px', marginLeft: '4px' }} />
                <span style={{ margin: '0 4px' }}>~</span>
                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                  style={{ padding: '3px 6px', border: '1px solid #d1d5db',
                    borderRadius: '3px', fontSize: '11px' }} />
              </label>
              <label title="Bar Resolution — 전략이 OnBar로 받는 분봉 해상도">Bar:
                <select value={barResolution}
                  onChange={e => {
                    const v = parseInt(e.target.value);
                    setBarResolution(v);
                    if (signalFrequency < v) setSignalFrequency(v);
                  }}
                  style={{ padding: '3px 6px', border: '1px solid #d1d5db',
                    borderRadius: '3px', fontSize: '11px', marginLeft: '4px', backgroundColor: 'white' }}>
                  <option value={1}>1분</option>
                  <option value={3}>3분</option>
                  <option value={5}>5분</option>
                  <option value={10}>10분</option>
                  <option value={15}>15분</option>
                  <option value={30}>30분</option>
                  <option value={60}>60분</option>
                </select>
              </label>
              <label title="Signal Frequency — 전략 Evaluate() 호출 주기">Signal:
                <select value={signalFrequency}
                  onChange={e => setSignalFrequency(parseInt(e.target.value))}
                  style={{ padding: '3px 6px', border: '1px solid #d1d5db',
                    borderRadius: '3px', fontSize: '11px', marginLeft: '4px', backgroundColor: 'white' }}>
                  {[1, 3, 5, 10, 15, 30, 60].filter(v => v >= barResolution).map(v => (
                    <option key={v} value={v}>{v}분</option>
                  ))}
                </select>
              </label>
              <button onClick={runTrace} disabled={loading || !prodId}
                style={{ padding: '4px 10px',
                  backgroundColor: loading ? '#9ca3af' : '#3b82f6',
                  color: 'white', border: 'none', borderRadius: '3px',
                  cursor: loading || !prodId ? 'not-allowed' : 'pointer', fontSize: '11px' }}>
                {loading ? '실행중...' : '추적 실행'}
              </button>
            </div>
          )}
        </div>

        {error && <div style={{ color: '#ef4444', fontSize: '11px', marginBottom: '8px' }}>{error}</div>}

        {!data && !loading && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '12px' }}>
            {showControls
              ? '종목·기간·Bar·Signal 입력 후 "추적 실행"을 누르면 IntradayService 진행 과정이 재현됩니다.'
              : '상단에서 "시뮬레이션 실행"을 누르면 트레이스 결과가 여기에 표시됩니다.'}
            <br />
            <span style={{ fontSize: '10px' }}>(셀·행 선택 후 Ctrl+C로 엑셀 붙여넣기 가능)</span>
          </div>
        )}

        {data && (
          <>
            {/* 요약 메타 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                          gap: '6px', marginBottom: '10px', fontSize: '11px' }}>
              <div><b>종목:</b> {data.prodId}</div>
              <div><b>기간:</b> {data.fromDate} ~ {data.toDate}</div>
              <div><b>거래일:</b> {(data.tradingDates ?? []).length}일</div>
              <div><b>Bar Resolution:</b> {data.barResolution}분</div>
              <div><b>Signal Frequency:</b> {data.signalFrequency}분</div>
              <div><b>전략:</b> {(data.strategyNames || []).join(', ')}</div>
              <div><b>분봉:</b> {data.bars?.length ?? 0}건</div>
              <div><b>Tick신호:</b> {data.ticks?.length ?? 0}건</div>
              <div><b>다수결:</b> {data.tickDecisions?.length ?? 0}건</div>
            </div>

            {/* 캔들차트 + Buy/Sell flags */}
            {chartOptions && (
              <div style={{ marginBottom: '12px', backgroundColor: 'white',
                border: '1px solid #d1d5db', borderRadius: '6px', padding: '8px' }}>
                <HighchartsReact highcharts={Highcharts} constructorType="stockChart" options={chartOptions} />
              </div>
            )}

            {/* 시점별 다수결 */}
            <div style={{ marginBottom: '12px' }}>
              <h4 style={{ margin: '0 0 6px 0', fontSize: '11px', fontWeight: 600, color: '#1e40af' }}>
                시점별 다수결 결과 ({data.tickDecisions?.length ?? 0}건)
                <span style={{ fontSize: '10px', color: '#6b7280', marginLeft: 8 }}>
                  (행 클릭 → 사용된 분봉 필터)
                </span>
              </h4>
              <div className="ag-theme-quartz" style={{ height: 220 }}>
                <AgGridReact
                  rowData={data.tickDecisions || []}
                  columnDefs={decisionColumnDefs as any}
                  defaultColDef={NO_SF}
                  rowHeight={24}
                  headerHeight={28}
                  rowSelection="single"
                  onRowClicked={(e) => { if (e.data?.tickTime) loadTickWindow(e.data.tickTime); }}
                  getRowStyle={(p: any) => selectedTickTime && p.data?.tickTime === selectedTickTime
                    ? { backgroundColor: '#fef3c7' } : undefined}
                  enableRangeSelection enableRangeHandle enableFillHandle
                  suppressCopyRowsToClipboard={false}
                  getContextMenuItems={copyMenu as any}
                  animateRows={false}
                />
              </div>
            </div>

            {/* 시점 × 전략 지표 */}
            <div style={{ marginBottom: '12px' }}>
              <h4 style={{ margin: '0 0 6px 0', fontSize: '11px', fontWeight: 600, color: '#1e40af' }}>
                시점 × 전략 지표 계산 결과 ({data.ticks?.length ?? 0}건)
                <span style={{ fontSize: '10px', color: '#6b7280', marginLeft: 8 }}>
                  (행 클릭 → 그 시점에 전략이 본 분봉 윈도우 표시)
                </span>
              </h4>
              <div className="ag-theme-quartz" style={{ height: 300 }}>
                <AgGridReact
                  rowData={data.ticks || []}
                  columnDefs={tickColumnDefs as any}
                  defaultColDef={NO_SF}
                  rowHeight={24}
                  headerHeight={28}
                  rowSelection="single"
                  onRowClicked={(e) => { if (e.data?.tickTime) loadTickWindow(e.data.tickTime); }}
                  getRowStyle={(p: any) => selectedTickTime && p.data?.tickTime === selectedTickTime
                    ? { backgroundColor: '#fef3c7' } : undefined}
                  enableRangeSelection enableRangeHandle enableFillHandle
                  suppressCopyRowsToClipboard={false}
                  getContextMenuItems={copyMenu as any}
                  animateRows={false}
                />
              </div>
            </div>

            {/* 사용된 분봉 윈도우 */}
            <div style={{ marginBottom: '12px' }}>
              <h4 style={{ margin: '0 0 6px 0', fontSize: '11px', fontWeight: 600, color: '#7c3aed' }}>
                {selectedTickTime
                  ? `사용된 분봉 윈도우 — tick ${selectedTickTime.split(' ')[1] ?? selectedTickTime} (${tickWindowBars.length}건)`
                  : '사용된 분봉 윈도우 (위에서 tick 행을 클릭하세요)'}
                {tickWindowLoading && <span style={{ marginLeft: 8, color: '#6b7280' }}>조회중...</span>}
              </h4>
              <div className="ag-theme-quartz" style={{ height: selectedTickTime ? 260 : 80 }}>
                <AgGridReact
                  rowData={tickWindowBars}
                  columnDefs={barColumnDefs as any}
                  defaultColDef={NO_SF}
                  rowHeight={22}
                  headerHeight={28}
                  enableRangeSelection enableRangeHandle enableFillHandle
                  suppressCopyRowsToClipboard={false}
                  getContextMenuItems={copyMenu as any}
                  animateRows={false}
                />
              </div>
            </div>

            {/* 기간 전체 분봉 */}
            <div>
              <h4 style={{ margin: '0 0 6px 0', fontSize: '11px', fontWeight: 600, color: '#1e40af' }}>
                기간 {data.barResolution ?? barResolution}분봉 ({data.bars?.length ?? 0}건)
                <span style={{ fontSize: '10px', color: '#6b7280', marginLeft: 8 }}>
                  {data.fromDate} ~ {data.toDate} · clsprc 기반 + MAX(vol) 차분 (전략 입력과 동일)
                </span>
              </h4>
              <div className="ag-theme-quartz" style={{ height: 260 }}>
                <AgGridReact
                  rowData={data.bars || []}
                  columnDefs={barColumnDefs as any}
                  defaultColDef={NO_SF}
                  rowHeight={22}
                  headerHeight={28}
                  enableRangeSelection enableRangeHandle enableFillHandle
                  suppressCopyRowsToClipboard={false}
                  getContextMenuItems={copyMenu as any}
                  animateRows={false}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IntradayTraceSection;
